import {
  type Query as TCubeQuery,
  TimeDimension,
  TimeDimensionGranularity,
  ResultSet,
  TQueryOrderObject,
  Filter,
  BinaryOperator,
} from "@cubejs-client/core"
import { TMetric } from "~/ui-rtk/constants/metrics-mapping"
import { SortingState } from "@tanstack/react-table"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"

import useDate from "~/ui-rtk/hooks/date"
const dayjs = useDate()

export function polyfillCubeQueryWithTimeDimension(
  cubeQuery: TMetric,
  {
    granularity,
    dateRange,
  }: {
    dateRange: [Date, Date]
    granularity?: TimeDimensionGranularity
  },
): TCubeQuery {
  return {
    ...cubeQuery,
    timeDimensions: (cubeQuery?.timeDimensions ?? []).map(
      (timeDimension: TimeDimension) => {
        const $timeDimension = {
          ...timeDimension,
          dateRange: [
            dayjs(dateRange[0]).format("YYYY-MM-DD"),
            dayjs(dateRange[1]).format("YYYY-MM-DD"),
          ],
        }

        if (granularity) {
          return {
            ...$timeDimension,
            granularity,
          }
        }

        return $timeDimension
      },
    ) as unknown as TimeDimension[],
  }
}

export function addPagination(
  cubeQuery: TCubeQuery,
  itemsPerPage?: number,
  offset?: number,
) {
  cubeQuery.limit = itemsPerPage
  cubeQuery.offset = offset
  cubeQuery.total = true

  return cubeQuery
}

export function addFilters(
  cubeQuery: TCubeQuery,
  CubeFilters?: TCubeFilterOptions,
  cubePrefix?: string,
) {
  const filters = cubeQuery.filters || []

  const addPrefix = (filter: Filter, prefix: string): Filter => {
    if ("and" in filter && filter.and) {
      filter.and = filter.and.map((f: Filter) => addPrefix(f, prefix))
    }

    if ("or" in filter && filter.or) {
      filter.or = filter.or.map((f: Filter) => addPrefix(f, prefix))
    }

    if (
      "member" in filter &&
      filter.member &&
      !filter.member.includes(prefix)
    ) {
      filter.member = `${prefix}.${filter.member}`
    }
    return filter
  }

  if (CubeFilters?.and) {
    if (cubePrefix) {
      filters.push({ and: CubeFilters.and.map(f => addPrefix(f, cubePrefix)) })
    } else {
      filters.push({ and: CubeFilters.and })
    }
  }

  if (CubeFilters?.extraFilters) {
    if (cubePrefix) {
      for (const filter of CubeFilters.extraFilters) {
        filters.push(addPrefix(filter, cubePrefix))
      }
    } else {
      filters.push(...CubeFilters.extraFilters)
    }
  }

  cubeQuery.filters = filters

  return cubeQuery
}

export function addSorting(
  cubeQuery: TCubeQuery,
  sorting?: SortingState,
  cubePrefix?: string,
  extraSorting?: Array<{ id: string; desc: boolean }>,
) {
  // Initialize order object if it doesn't exist
  if (!cubeQuery.order) {
    cubeQuery.order = {} as TQueryOrderObject
  }

  const cubeQueryOrder = cubeQuery.order as TQueryOrderObject

  // Apply user sorting first (highest priority)
  if (sorting && sorting.length > 0) {
    sorting.forEach(({ id, desc }: { id: string; desc: boolean }) => {
      const orderKey = cubePrefix ? `${cubePrefix}.${id}` : id
      cubeQueryOrder[orderKey] = desc ? "desc" : "asc"
    })
  }

  // Apply extra sorting (lowest priority, tiebreakers)
  if (extraSorting && extraSorting.length > 0) {
    extraSorting.forEach(({ id, desc }: { id: string; desc: boolean }) => {
      // Only add if not already set by user sorting
      const orderKey = cubePrefix ? `${cubePrefix}.${id}` : id
      if (!(orderKey in cubeQueryOrder)) {
        cubeQueryOrder[orderKey] = desc ? "desc" : "asc"
      }
    })
  }

  return cubeQuery
}

// Result rows key time dimensions as `<dimension>.<granularity>`. Queries
// always SEND granularity 'week', but the cube server rewrites it to the
// company's custom week granularity (e.g. `metrics.date.week_sunday`) when a
// non-default reporting week start is configured. Resolve the actual key from
// the rows instead of assuming the requested granularity.
export function resolveTimeDimensionKey(
  rows: Array<Record<string, unknown>> | null | undefined,
  dimensionPrefix: string,
  preferredKey: string,
): string {
  const first = rows?.[0]
  if (!first || preferredKey in first) return preferredKey
  return (
    Object.keys(first).find(key => key.startsWith(`${dimensionPrefix}.`)) ??
    preferredKey
  )
}

export function getPivotData(
  resultSet: ResultSet<Record<string, unknown>>,
  query: TCubeQuery | TCubeQuery[],
) {
  if (Array.isArray(query)) {
    const decomposedPivotData = resultSet
      .decompose()
      .map(_resultSet => _resultSet.tablePivot())
    return decomposedPivotData.reduce((acc, pivot) => [...acc, ...pivot], [])
  }

  return resultSet.tablePivot()
}

export const convertToCubeFilters = (
  queryPrefix?: string,
  filters?: Record<string, string | string[] | undefined | null>,
): Filter[] =>
  filters
    ? (Object.keys(filters)
        .map(key => {
          if (!filters[key]) {
            return undefined
          }
          return {
            member: queryPrefix ? `${queryPrefix}.${key}` : key,
            operator: "equals" as BinaryOperator,
            values: Array.isArray(filters[key]) ? filters[key] : [filters[key]],
          }
        })
        .filter(f => !!f) as Filter[])
    : []
